import React, { memo } from 'react';
import { paths } from 'client/shared/Paths';
import { useT } from 'client/i18n/hooks/useT';
import { MarketingImage } from 'components/MarketingPagesShared/MarketingImage';
import { MarketingVideo } from 'components/MarketingPagesShared/MarketingVideo';
import { MarketingAnchor } from 'components/MarketingPagesShared/styles';
import {
  FeaturesCaptionedImage,
  FeaturesSection,
  FeaturesSectionSubtitle,
  FeaturesSectionTitle,
  FeaturesHorizontalCaption,
  FeaturesSectionDescription,
} from '../../styles';

function FeaturesCreationSectionView() {
  const t = useT();
  return (
    <FeaturesSection>
      <FeaturesSectionTitle>
        {t(
          'FeaturesScreen.FeaturesCreationSection.Title',
          'Convenient creation tools',
          'Anchor features page creation section title'
        )}
      </FeaturesSectionTitle>
      <FeaturesCaptionedImage>
        <MarketingImage
          alt={t(
            'FeaturesScreen.FeaturesCreationSection.RecordingImageAlt',
            'A rendering of the Anchor episode builder',
            'Anchor features page creation section, recording feature image screenreader information'
          )}
          imagePath="features/creation-recording"
          width={970}
          height={545}
        />
        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesCreationSection.RecordingTitle',
              'Flexible recording and uploading',
              'Anchor features page creation section, recording feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesCreationSection.RecordingDescription',
              'Recording tools capture audio straight from your phone, tablet, or computer, and sync them across all devices. Or, you can import existing audio or video.',
              'Anchor features page creation section, recording feature description'
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>

      <FeaturesCaptionedImage>
        <MarketingImage
          alt={t(
            'FeaturesScreen.FeaturesCreationSection.EpisodeBuilderImageAlt',
            'A rendering of the Anchor audio segment arrangement tool',
            'Anchor features page creation section, episode builder feature image screenreader information'
          )}
          imagePath="features/creation-episode-builder"
          width={970}
          height={545}
        />
        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesCreationSection.EpisodeBuilderTitle',
              'Intuitive episode building',
              'Anchor features page creation section, episode builder feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesCreationSection.EpisodeBuilderDescription',
              "Easy-to-visualize building blocks of audio segments don't require any editing. Record your audio, arrange your segments, add transitions, and you're set.",
              'Anchor features page creation section, episode builder feature description'
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>

      <FeaturesCaptionedImage>
        <MarketingVideo
          width={970}
          height={546}
          title={t(
            'FeaturesScreen.FeaturesCreationSection.MusicAndTalkVideoTitle',
            'Anchor: Introducing shows with Spotify music',
            'Anchor features page creation section, music feature video title'
          )}
          youTubeVideoId="chMtuVpP7lk"
        >
          <MarketingImage
            alt={t(
              'FeaturesScreen.FeaturesCreationSection.MusicAndTalkImageAlt',
              'Man sitting on the front steps listening to music and recording into his phone',
              'Anchor features page creation section, music feature image screenreader information'
            )}
            imagePath="features/creation-music-integration"
            width={970}
            height={546}
          />
        </MarketingVideo>

        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesCreationSection.MusicAndTalkTitle',
              'Exclusive music integration',
              'Anchor features page creation section, music feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesCreationSection.MusicAndTalkDescription',
              'Add any full tracks from Spotify to your episodes, and combine music and conversation to explore the {marketingLink}.',
              'Anchor features page creation section, music feature description',
              {
                marketingLink: (
                  <MarketingAnchor
                    href={paths.blog.music}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(
                      'FeaturesScreen.FeaturesCreationSection.AudioPossibilitiesLinkText',
                      'full possibilities of audio',
                      'Anchor features page creation section, audio possibilities blog article link text'
                    )}
                  </MarketingAnchor>
                ),
              }
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>

      <FeaturesCaptionedImage>
        <MarketingImage
          alt={t(
            'FeaturesScreen.FeaturesCreationSection.RecordWithFriendImageAlt',
            'An image showing the Anchor recording tool on an iPhone, two images of people at different locations collaborating on a podcast',
            'Anchor features page creation section, collaborate recording feature image screenreader information'
          )}
          imagePath="features/creation-collaboration"
          width={970}
          height={545}
        />
        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesCreationSection.RecordWithFriendsTitle',
              'Collaborate with co-hosts, guests, and listeners',
              'Anchor features page creation section, collaborate recording feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesCreationSection.RecordWithFriendsDescription',
              'Multiple people can record with you at the same time. You can also feature your listeners by inviting them to send you a voice message, which you can include in future episodes.',
              'Anchor features page creation section, collaborate recording feature description'
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>

      <FeaturesCaptionedImage>
        <MarketingImage
          alt={t(
            'FeaturesScreen.FeaturesCreationSection.CoverArtCreatorImageAlt',
            'A rendering of the Anchor cover art makers',
            'Anchor features page creation section, cover art creator feature image screenreader information'
          )}
          imagePath="features/creation-cover-art"
          width={970}
          height={545}
        />
        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesCreationSection.CoverArtCreatorTitle',
              'Cover art creator',
              'Anchor features page creation section, cover art creator feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesCreationSection.CoverArtCreatorDescription',
              'Generate custom cover art in seconds, using images and fonts to help ensure your podcast has an {marketingLink}.',
              'Anchor features page creation section, cover art creator feature description',
              {
                marketingLink: (
                  <MarketingAnchor
                    href={paths.blog.coverartDosAndDonts}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(
                      'FeaturesScreen.FeaturesCreationSection.CoverArtCreatorLinkText',
                      'eye-catching cover',
                      'cover art creator blog post link text'
                    )}
                  </MarketingAnchor>
                ),
              }
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>
    </FeaturesSection>
  );
}

export const FeaturesCreationSection = memo(FeaturesCreationSectionView);
